import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  CardBodyComponent,
  CardComponent,
  IMAGEKIT_THUMBNAIL_CONFIG,
  ImageTile,
  ImageTileComponent,
  PaginationComponent,
  StateMessageComponent,
  toImageKitThumbnailUrl,
} from '@groundworks/ngx-groundworks-ui';
import { Gallery, GalleryModule, ImageItem } from 'ng-gallery';
import { Lightbox, LightboxModule } from 'ng-gallery/lightbox';
import { map, Observable } from 'rxjs';
import { observeUserRoles$ } from '../../models/roles.model';
import { AttachmentInfo, QuoteInfo } from '../../sdk/sdk';
import { AttachmentService } from '../../services/attachment.service';
import { FileDownloadService } from '../../services/file-download.service';
import { FileUtilityService } from '../../services/file-utility.service';
import { ModalService } from '../../services/modal.service';
import { pageCount, pageRangeLabel, pageSlice } from '../../utils/paginate';

/** Attachments shown per page in each tab (MOD-5671). Change it here only. */
export const ATTACHMENTS_PAGE_SIZE = 50;

type TabType = 'documents' | 'pictures';

@Component({
  selector: 'app-attachments',
  imports: [
    CardComponent,
    CardBodyComponent,
    StateMessageComponent,
    ImageTileComponent,
    PaginationComponent,
    GalleryModule,
    LightboxModule,
  ],
  templateUrl: './attachments.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachmentsComponent {
  private readonly gallery = inject(Gallery);
  private readonly lightbox = inject(Lightbox);
  private readonly modalService = inject(ModalService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly fileUtilityService = inject(FileUtilityService);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly thumbnailConfig = inject(IMAGEKIT_THUMBNAIL_CONFIG);

  // Signal inputs
  readonly attachments = input<AttachmentInfo[]>([]);
  readonly isLoading = input(false);
  readonly hasError = input(false);
  readonly opportunityId = input<string | null>(null);
  readonly appointmentId = input<string | null>(null);
  readonly quoteInfo = input<QuoteInfo[] | null>(null);

  // Outputs
  readonly attachmentDeleted = output<string>();
  readonly attachmentAdded = output<void>();
  readonly attachmentUpdated = output<AttachmentInfo>();

  // Internal state - writable signal to handle local mutations (delete)
  private readonly _attachments = signal<AttachmentInfo[]>([]);
  private readonly userRoles$ = observeUserRoles$();

  readonly activeTab = signal<TabType>('pictures');
  viewMode: 'grid' | 'list' = 'grid';

  private readonly galleryId = 'attachments-gallery';
  canDelete = false;

  // Computed signals for filtered lists
  readonly pictures = computed(() =>
    this._attachments().filter((a) => this.fileUtilityService.isImageFile(a))
  );

  readonly documents = computed(() =>
    this._attachments().filter((a) => !this.fileUtilityService.isImageFile(a))
  );

  readonly picturesCount = computed(() => this.pictures().length);
  readonly documentsCount = computed(() => this.documents().length);

  readonly currentItems = computed(() =>
    this.activeTab() === 'pictures' ? this.pictures() : this.documents()
  );

  readonly currentPage = signal(1);

  readonly totalPages = computed(() => pageCount(this.currentItems().length, ATTACHMENTS_PAGE_SIZE));

  // Derived rather than written back, so a delete that empties the last page needs no extra code.
  readonly effectivePage = computed(() => Math.min(this.currentPage(), this.totalPages()));

  readonly pagedItems = computed(() =>
    pageSlice(this.currentItems(), this.effectivePage(), ATTACHMENTS_PAGE_SIZE)
  );

  readonly rangeLabel = computed(() =>
    pageRangeLabel(this.currentItems().length, this.effectivePage(), ATTACHMENTS_PAGE_SIZE)
  );

  private readonly attachmentsScroll = viewChild<ElementRef<HTMLElement>>('attachmentsScroll');

  readonly state = computed(() => {
    if (this.isLoading()) return 'loading';
    if (this.hasError()) return 'error';
    if (this._attachments().length === 0) return 'no-matches';
    return 'matches';
  });

  get canDeleteAttachments$(): Observable<boolean> {
    return this.userRoles$.pipe(map((roles) => roles.SalesManager || roles.SuperUser || roles.ServiceManager));
  }

  constructor() {
    // Sync input to internal signal and initialize gallery when attachments change
    effect(() => {
      const attachments = this.attachments();
      this._attachments.set(attachments);
      this.initializeGallery();
    });

    // Reset the page on a reload (e.g. after an upload, which lands first, newest first), not on
    // every input change: the parent also writes edits and deletes back into `attachments`.
    effect(() => {
      if (this.isLoading()) {
        this.currentPage.set(1);
      }
    });

    // Subscribe to role changes for delete permission
    this.canDeleteAttachments$.subscribe((canDelete) => {
      this.canDelete = canDelete;
    });
  }

  selectTab(tab: TabType): void {
    this.activeTab.set(tab);
    this.currentPage.set(1);
    this.scrollToTop();
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.scrollToTop();
  }

  // Both tabs share one scroll window; start each page and each tab at its first tile.
  private scrollToTop(): void {
    this.attachmentsScroll()?.nativeElement.scrollTo({ top: 0 });
  }

  openInNewTab(url: string) {
    window.open(url, '_blank');
  }


  toTileData(attachment: AttachmentInfo): ImageTile {
    const menuOptions = [
      {
        label: 'Download',
        action: () => this.downloadAttachment(attachment),
      },
      {
        label: 'Edit',
        action: () => this.editAttachment(attachment),
      },
    ];

    if (this.canDelete) {
      menuOptions.push({
        label: 'Delete',
        action: () => this.deleteAttachment(attachment.id!),
      });
    }
    return {
      menuOptions: menuOptions,
      title: attachment.attachmentGroup ?? 'No Group Selected',
      description: attachment.description ?? 'No Description Available',
      fileUrl: attachment.filePath!,
      timestamp: attachment.createdAt ?? new Date(),
      type: this.fileUtilityService.getFileType(attachment),
      hasFlag: false,
      thumbnailUrl: attachment.filePath,
    };
  }

  downloadAttachment(attachment: AttachmentInfo) {
    if (!attachment.filePath) {
      return;
    }
    this.fileDownloadService.downloadFile(attachment.filePath, attachment.description || 'attachment');
  }

  editAttachment(attachment: AttachmentInfo) {
    const editModal = this.modalService.openAttachmentEditModal(
      attachment.id!,
      attachment.attachmentGroupId ?? null,
      attachment.description ?? ''
    );
    editModal.result
      .then((result) => {
        if (result?.attachmentId) {
          const originalAttachment = this._attachments().find((a) => a.id === result.attachmentId);
          if (originalAttachment) {
            const updatedAttachment = new AttachmentInfo({
              ...originalAttachment,
              attachmentGroupId: result.groupId ?? undefined,
              attachmentGroup: result.groupName ?? undefined,
              description: result.description ?? undefined,
            });
            this.attachmentUpdated.emit(updatedAttachment);
          }
        }
      })
      .catch(() => undefined);
  }

  private initializeGallery() {
    const galleryItems = this._attachments()
      .filter((a) => a.filePath && this.fileUtilityService.isImageFile(a))
      .map(
        (a) =>
          new ImageItem({
            src: a.filePath!,
            thumb: toImageKitThumbnailUrl(a.filePath, this.thumbnailConfig),
            alt: a.description ?? 'Attachment',
          })
      );

    const galleryRef = this.gallery.ref(this.galleryId, {
      counter: true,
      thumbs: true,
      thumbPosition: 'bottom',
      loadingStrategy: 'lazy',
      loadingAttr: 'lazy',
    });
    galleryRef.load(galleryItems);
  }

  onTileClick(tileData: ImageTile) {
    const attachment = this._attachments().find((a) => a.filePath === tileData.fileUrl);

    if (!attachment) {
      return;
    }

    if (this.fileUtilityService.isImageFile(attachment)) {
      // Every picture, not just the current page: the gallery holds them all, so the lightbox
      // opens on the clicked picture and steps past page boundaries.
      const imageAttachments = this._attachments().filter(
        (a) => a.filePath && this.fileUtilityService.isImageFile(a)
      );
      const index = imageAttachments.findIndex((a) => a.filePath === tileData.fileUrl);

      if (index !== -1) {
        this.lightbox.open(index, this.galleryId);
      }
    } else {
      this.openInNewTab(tileData.fileUrl);
    }
  }

  onAddAttachment() {
    const opportunityId = this.opportunityId();
    const appointmentId = this.appointmentId();
    const quoteInfo = this.quoteInfo();

    if (!opportunityId || !appointmentId) {
      console.error('Opportunity ID and Appointment ID are required to upload attachments.');
      return;
    }

    const fileModal = this.modalService.openAttachmentUploadModal();

    fileModal.result
      .then((files: File[] | undefined) => {
        if (!files || files.length === 0) {
          return;
        }
        const detailsModal = this.modalService.openAttachmentUploadDetailsModal(
          files,
          opportunityId,
          appointmentId,
          quoteInfo![0].id
        );
        detailsModal.result
          .then((result) => {
            if (result === undefined || result?.reload === true) {
              this.attachmentAdded.emit();
            }
          })
          .catch(() => undefined);
      })
      .catch(() => undefined);
  }

  deleteAttachment(attachmentId: string) {
    if (!attachmentId) {
      return;
    }

    const confirmModal = this.modalService.openConfirmModal(
      'Delete File',
      'Are you sure you want to delete this file? This action cannot be undone.',
      'Delete',
      'Cancel',
      'alert alert-danger'
    );

    confirmModal.result
      .then((confirmed: boolean) => {
        if (confirmed) {
          this.attachmentService.deleteAttachment(attachmentId).subscribe({
            next: () => {
              this._attachments.update((attachments) =>
                attachments.filter((a) => a.id !== attachmentId)
              );
              this.initializeGallery();
              this.attachmentDeleted.emit(attachmentId);
            },
            error: (error) => {
              console.error('Error deleting attachment:', error);
            },
          });
        }
      })
      .catch(() => {
        // User dismissed the modal, do nothing
      });
  }
}
